import type { ColInfo, RowInfo, WorkSheet } from 'xlsx';
import { CFB, read, utils } from 'xlsx';
import type { Alignment, Cell, ColumnData, Fill, Font, MergedCell, RowData } from 'api';
import type { SpreadsheetNoteData, SpreadsheetNoteXlsxImage } from 'components/SpreadsheetNotes/types';

type XlsxColor = {
  auto?: boolean;
  indexed?: number;
  rgb?: string;
  theme?: number;
  tint?: number;
};

type XlsxBorderSide = {
  color?: string | null;
  style?: string | null;
};

type XlsxCellBorder = {
  bottom?: XlsxBorderSide | null;
  left?: XlsxBorderSide | null;
  right?: XlsxBorderSide | null;
  top?: XlsxBorderSide | null;
};

type XlsxRawBorderSide = {
  color?: XlsxColor;
  style?: string;
};

type XlsxRawCellStyle = {
  alignment?: {
    horizontal?: string;
    vertical?: string;
    wrapText?: boolean;
  };
  bgColor?: XlsxColor;
  border?: {
    bottom?: XlsxRawBorderSide;
    left?: XlsxRawBorderSide;
    right?: XlsxRawBorderSide;
    top?: XlsxRawBorderSide;
  };
  fgColor?: XlsxColor;
  fill?: {
    bgColor?: XlsxColor;
    fgColor?: XlsxColor;
    patternType?: string;
  };
  font?: {
    b?: boolean;
    bold?: boolean;
    color?: XlsxColor;
    i?: boolean;
    italic?: boolean;
    name?: string;
    sz?: number;
    u?: boolean | string;
    underline?: boolean | string;
  };
  patternType?: string;
};

type XlsxCellOverride = Partial<Cell> & {
  border?: XlsxCellBorder | null;
};

type XlsxSheetPresentationOverride = {
  cells: Record<string, XlsxCellOverride>;
  columnData: Record<number, ColumnData>;
  images: SpreadsheetNoteXlsxImage[];
  rowData: Record<number, RowData>;
};

export type XlsxPresentationOverrides = {
  sheetNames: string[];
  sheets: Record<string, XlsxSheetPresentationOverride>;
};

type XlsxRawCellStyleMap = Record<string, XlsxRawCellStyle>;

type XlsxWorkbookStyleContext = {
  sheetStyles: Record<string, XlsxRawCellStyleMap>;
  themeColors: string[];
};

type XlsxWorkbookRelationship = {
  id: string;
  target: string;
  targetFileName: string;
  type?: string;
};

type XlsxWorkbookSheetReference = {
  name: string;
  relationshipId?: string;
};

type ApplyXlsxPresentationOverridesOptions = {
  maxEmptyMergedRowSpan?: number;
};

type XlsxCfbFileEntry = {
  content?: Uint8Array | number[];
  name: string;
};

type XlsxCfbWorkbook = {
  FileIndex: XlsxCfbFileEntry[];
};

const EXCEL_DEFAULT_THEME_COLORS = [
  '#FFFFFF',
  '#000000',
  '#EEECE1',
  '#1F497D',
  '#4F81BD',
  '#C0504D',
  '#9BBB59',
  '#8064A2',
  '#4BACC6',
  '#F79646',
  '#0000FF',
  '#800080',
];

const EXCEL_INDEXED_COLORS: Record<number, string> = {
  0: '#000000',
  1: '#FFFFFF',
  2: '#FF0000',
  3: '#00FF00',
  4: '#0000FF',
  5: '#FFFF00',
  6: '#FF00FF',
  7: '#00FFFF',
  8: '#000000',
  9: '#FFFFFF',
  10: '#FF0000',
  11: '#00FF00',
  12: '#0000FF',
  13: '#FFFF00',
  14: '#FF00FF',
  15: '#00FFFF',
  16: '#800000',
  17: '#008000',
  18: '#000080',
  19: '#808000',
  20: '#800080',
  21: '#008080',
  22: '#C0C0C0',
  23: '#808080',
  24: '#9999FF',
  25: '#993366',
  26: '#FFFFCC',
  27: '#CCFFFF',
  28: '#660066',
  29: '#FF8080',
  30: '#0066CC',
  31: '#CCCCFF',
  32: '#000080',
  33: '#FF00FF',
  34: '#FFFF00',
  35: '#00FFFF',
  36: '#800080',
  37: '#800000',
  38: '#008080',
  39: '#0000FF',
  40: '#00CCFF',
  41: '#CCFFFF',
  42: '#CCFFCC',
  43: '#FFFF99',
  44: '#99CCFF',
  45: '#FF99CC',
  46: '#CC99FF',
  47: '#FFCC99',
  48: '#3366FF',
  49: '#33CCCC',
  50: '#99CC00',
  51: '#FFCC00',
  52: '#FF9900',
  53: '#FF6600',
  54: '#666699',
  55: '#969696',
  56: '#003366',
  57: '#339966',
  58: '#003300',
  59: '#333300',
  60: '#993300',
  61: '#993366',
  62: '#333399',
  63: '#333333',
};

const XLSX_MAX_PRESENTATION_ROWS = 1000;
const XLSX_MAX_PRESENTATION_COLUMNS = 200;
const EXCEL_ROW_HEIGHT_TO_PX = 4 / 3;
const EXCEL_COLUMN_WIDTH_TO_PX = 7;
const EXCEL_COLUMN_WIDTH_PADDING_PX = 5;
const XLSX_MIN_COLUMN_WIDTH = 0;
const XLSX_MAX_COLUMN_WIDTH = 45;
const XLSX_MIN_ROW_HEIGHT = 0;
const XLSX_MAX_ROW_HEIGHT = 90;
const XLSX_MAX_EMPTY_MERGED_ROW_SPAN = 20;
const EMU_PER_PIXEL = 9525;
const BASE64_CHUNK_SIZE = 0x8000;

const XLSX_IMAGE_CONTENT_TYPES: Record<string, string> = {
  bmp: 'image/bmp',
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const parseBooleanAttribute = (value: string | null) => value === '1' || value === 'true';

const normalizeHexColor = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const normalizedValue = value.replace('#', '').trim();
  const hexValue = normalizedValue.length === 8 ? normalizedValue.slice(2) : normalizedValue;

  if (!/^[\dA-Fa-f]{6}$/.test(hexValue)) {
    return undefined;
  }

  return `#${hexValue.toUpperCase()}`;
};

const applyTintToHexColor = (hexColor: string, tint?: number) => {
  if (!tint) {
    return hexColor;
  }

  const normalizedColor = normalizeHexColor(hexColor);

  if (!normalizedColor) {
    return hexColor;
  }

  const channels = [1, 3, 5].map(startIndex => parseInt(normalizedColor.slice(startIndex, startIndex + 2), 16));
  const tintedChannels = channels.map(channel => {
    const tintedValue = tint < 0 ? channel * (1 + tint) : channel + (255 - channel) * tint;

    return Math.min(Math.max(Math.round(tintedValue), 0), 255)
      .toString(16)
      .padStart(2, '0');
  });

  return `#${tintedChannels.join('').toUpperCase()}`;
};

const normalizeColor = (color?: XlsxColor, themeColors = EXCEL_DEFAULT_THEME_COLORS) => {
  if (!color || color.auto) {
    return undefined;
  }

  const rgbColor = normalizeHexColor(color.rgb);

  if (rgbColor) {
    return applyTintToHexColor(rgbColor, color.tint);
  }

  if (color.indexed != null) {
    const indexedColor = EXCEL_INDEXED_COLORS[color.indexed];

    return indexedColor ? applyTintToHexColor(indexedColor, color.tint) : undefined;
  }

  if (color.theme != null) {
    const themeColor = themeColors[color.theme] ?? EXCEL_DEFAULT_THEME_COLORS[color.theme];

    return themeColor ? applyTintToHexColor(themeColor, color.tint) : undefined;
  }

  return undefined;
};

const getDirectChildrenByLocalName = (node: Element, localName: string) =>
  Array.from(node.children).filter(child => child.localName === localName);

const getDirectChildByLocalName = (node: Element, localName: string) =>
  getDirectChildrenByLocalName(node, localName)[0];

const getFirstElementByLocalName = (node: ParentNode, localName: string) =>
  Array.from(node.querySelectorAll('*')).find(child => child.localName === localName);

const getElementsByLocalName = (node: ParentNode, localName: string) =>
  Array.from(node.querySelectorAll('*')).filter(child => child.localName === localName);

const parseXmlDocument = (xml?: string) => {
  if (!xml || typeof DOMParser === 'undefined') {
    return undefined;
  }

  const document = new DOMParser().parseFromString(xml, 'application/xml');

  if (document.querySelector('parsererror')) {
    return undefined;
  }

  return document;
};

const getCfbTextFile = (workbook: XlsxCfbWorkbook, fileName: string) => {
  const file = workbook.FileIndex.find(
    entry => entry.name === fileName || entry.name.endsWith(`/${fileName}`) || entry.name.split('/').pop() === fileName
  );

  if (!file?.content) {
    return undefined;
  }

  return new TextDecoder().decode(file.content instanceof Uint8Array ? file.content : new Uint8Array(file.content));
};

const getCfbBinaryFile = (workbook: XlsxCfbWorkbook, fileName: string) => {
  const file = workbook.FileIndex.find(
    entry => entry.name === fileName || entry.name.endsWith(`/${fileName}`) || entry.name.split('/').pop() === fileName
  );

  if (!file?.content) {
    return undefined;
  }

  return file.content instanceof Uint8Array ? file.content : new Uint8Array(file.content);
};

const getTargetFileName = (target: string) => target.split('/').filter(Boolean).pop() ?? '';

const getRelationshipFileName = (fileName: string) => {
  const targetFileName = getTargetFileName(fileName);

  return targetFileName ? `${targetFileName}.rels` : '';
};

const getNamespacedAttribute = (node: Element | undefined, attributeName: string) =>
  node?.getAttribute(attributeName) ?? node?.getAttribute(`r:${attributeName}`) ?? undefined;

const getImageContentType = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  return extension ? XLSX_IMAGE_CONTENT_TYPES[extension] : undefined;
};

const uint8ArrayToBase64 = (bytes: Uint8Array) => {
  let binary = '';

  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_SIZE) {
    const chunk = bytes.subarray(offset, offset + BASE64_CHUNK_SIZE);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

const emuToPx = (value?: string | null) => {
  const numericValue = value == null ? NaN : Number(value);

  return Number.isFinite(numericValue) ? numericValue / EMU_PER_PIXEL : 0;
};

const getPositiveSize = (value: number) => (Number.isFinite(value) && value > 0 ? value : undefined);

const getXlsxCfbWorkbook = (arrayBuffer: ArrayBuffer) => {
  try {
    return CFB.read(new Uint8Array(arrayBuffer), { type: 'array' }) as XlsxCfbWorkbook;
  } catch {
    return undefined;
  }
};

const getColorFromXmlNode = (node?: Element): XlsxColor | undefined => {
  if (!node) {
    return undefined;
  }

  const rgb = node.getAttribute('rgb');
  const indexed = node.getAttribute('indexed');
  const theme = node.getAttribute('theme');
  const tint = node.getAttribute('tint');
  const auto = node.getAttribute('auto');

  return {
    auto: parseBooleanAttribute(auto),
    indexed: indexed == null ? undefined : Number(indexed),
    rgb: rgb ?? undefined,
    theme: theme == null ? undefined : Number(theme),
    tint: tint == null ? undefined : Number(tint),
  };
};

const getThemeColorFromXmlNode = (node?: Element) => {
  const srgbColor = node ? getFirstElementByLocalName(node, 'srgbClr') : undefined;
  const systemColor = node ? getFirstElementByLocalName(node, 'sysClr') : undefined;

  return normalizeHexColor(srgbColor?.getAttribute('val') ?? systemColor?.getAttribute('lastClr') ?? undefined);
};

const getWorkbookThemeColors = (workbook: XlsxCfbWorkbook) => {
  const themeDocument = parseXmlDocument(getCfbTextFile(workbook, 'theme1.xml'));
  const colorScheme = themeDocument ? getFirstElementByLocalName(themeDocument, 'clrScheme') : undefined;

  if (!colorScheme) {
    return EXCEL_DEFAULT_THEME_COLORS;
  }

  const themeColorOrder = [
    'lt1',
    'dk1',
    'lt2',
    'dk2',
    'accent1',
    'accent2',
    'accent3',
    'accent4',
    'accent5',
    'accent6',
    'hlink',
    'folHlink',
  ];
  const themeColors = themeColorOrder.map(themeColorName =>
    getThemeColorFromXmlNode(getDirectChildByLocalName(colorScheme, themeColorName))
  );

  return themeColors.every(Boolean) ? (themeColors as string[]) : EXCEL_DEFAULT_THEME_COLORS;
};

const parseFontStyle = (fontNode: Element): XlsxRawCellStyle['font'] => {
  const sizeValue = getDirectChildByLocalName(fontNode, 'sz')?.getAttribute('val');
  const fontName = getDirectChildByLocalName(fontNode, 'name')?.getAttribute('val') ?? undefined;

  return {
    b: Boolean(getDirectChildByLocalName(fontNode, 'b')),
    color: getColorFromXmlNode(getDirectChildByLocalName(fontNode, 'color')),
    i: Boolean(getDirectChildByLocalName(fontNode, 'i')),
    name: fontName,
    sz: sizeValue == null ? undefined : Number(sizeValue),
    u: Boolean(getDirectChildByLocalName(fontNode, 'u')),
  };
};

const parseFillStyle = (fillNode: Element): XlsxRawCellStyle => {
  const patternFill = getDirectChildByLocalName(fillNode, 'patternFill');

  if (!patternFill) {
    return {};
  }

  return {
    fill: {
      bgColor: getColorFromXmlNode(getDirectChildByLocalName(patternFill, 'bgColor')),
      fgColor: getColorFromXmlNode(getDirectChildByLocalName(patternFill, 'fgColor')),
      patternType: patternFill.getAttribute('patternType') ?? undefined,
    },
  };
};

const parseBorderSideStyle = (borderNode?: Element): XlsxRawBorderSide | undefined => {
  if (!borderNode) {
    return undefined;
  }

  const style = borderNode.getAttribute('style') ?? undefined;

  if (!style) {
    return undefined;
  }

  return {
    color: getColorFromXmlNode(getDirectChildByLocalName(borderNode, 'color')),
    style,
  };
};

const parseBorderStyle = (borderNode: Element): XlsxRawCellStyle['border'] => ({
  bottom: parseBorderSideStyle(getDirectChildByLocalName(borderNode, 'bottom')),
  left: parseBorderSideStyle(getDirectChildByLocalName(borderNode, 'left')),
  right: parseBorderSideStyle(getDirectChildByLocalName(borderNode, 'right')),
  top: parseBorderSideStyle(getDirectChildByLocalName(borderNode, 'top')),
});

const parseAlignmentStyle = (alignmentNode?: Element): XlsxRawCellStyle['alignment'] | undefined => {
  if (!alignmentNode) {
    return undefined;
  }

  return {
    horizontal: alignmentNode.getAttribute('horizontal') ?? undefined,
    vertical: alignmentNode.getAttribute('vertical') ?? undefined,
    wrapText: parseBooleanAttribute(alignmentNode.getAttribute('wrapText')),
  };
};

const mergeRawCellStyles = (
  baseStyle?: XlsxRawCellStyle,
  overrideStyle?: XlsxRawCellStyle
): XlsxRawCellStyle | undefined => {
  if (!baseStyle && !overrideStyle) {
    return undefined;
  }

  return {
    alignment: {
      ...baseStyle?.alignment,
      ...overrideStyle?.alignment,
    },
    bgColor: overrideStyle?.bgColor ?? baseStyle?.bgColor,
    border: {
      ...baseStyle?.border,
      ...overrideStyle?.border,
    },
    fgColor: overrideStyle?.fgColor ?? baseStyle?.fgColor,
    fill: {
      ...baseStyle?.fill,
      ...overrideStyle?.fill,
    },
    font: {
      ...baseStyle?.font,
      ...overrideStyle?.font,
    },
    patternType: overrideStyle?.patternType ?? baseStyle?.patternType,
  };
};

const getDirectChildStyleElements = (document: Document, parentName: string, childName: string) => {
  const parent = getFirstElementByLocalName(document, parentName);

  return parent ? getDirectChildrenByLocalName(parent, childName) : [];
};

const getWorkbookCellXfs = (workbook: XlsxCfbWorkbook) => {
  const stylesDocument = parseXmlDocument(getCfbTextFile(workbook, 'styles.xml'));

  if (!stylesDocument) {
    return [];
  }

  const fonts = getDirectChildStyleElements(stylesDocument, 'fonts', 'font').map(parseFontStyle);
  const fills = getDirectChildStyleElements(stylesDocument, 'fills', 'fill').map(parseFillStyle);
  const borders = getDirectChildStyleElements(stylesDocument, 'borders', 'border').map(parseBorderStyle);

  return getDirectChildStyleElements(stylesDocument, 'cellXfs', 'xf').map(xfNode => {
    const fontId = Number(xfNode.getAttribute('fontId') ?? 0);
    const fillId = Number(xfNode.getAttribute('fillId') ?? 0);
    const borderId = Number(xfNode.getAttribute('borderId') ?? 0);
    const applyFont = parseBooleanAttribute(xfNode.getAttribute('applyFont'));
    const applyFill = parseBooleanAttribute(xfNode.getAttribute('applyFill'));
    const applyBorder = parseBooleanAttribute(xfNode.getAttribute('applyBorder'));
    const alignment = parseAlignmentStyle(getDirectChildByLocalName(xfNode, 'alignment'));

    return (
      mergeRawCellStyles(
        mergeRawCellStyles(
          fontId > 0 || applyFont ? { font: fonts[fontId] } : undefined,
          fillId > 0 || applyFill ? fills[fillId] : undefined
        ),
        mergeRawCellStyles(
          borderId > 0 || applyBorder ? { border: borders[borderId] } : undefined,
          alignment ? { alignment } : undefined
        )
      ) ?? {}
    );
  });
};

const getWorkbookSheetReferences = (workbook: XlsxCfbWorkbook): XlsxWorkbookSheetReference[] => {
  const workbookDocument = parseXmlDocument(getCfbTextFile(workbook, 'workbook.xml'));
  const sheetsNode = workbookDocument ? getFirstElementByLocalName(workbookDocument, 'sheets') : undefined;

  if (!sheetsNode) {
    return [];
  }

  return getDirectChildrenByLocalName(sheetsNode, 'sheet')
    .map(sheetNode => ({
      name: sheetNode.getAttribute('name') ?? '',
      relationshipId: sheetNode.getAttribute('r:id') ?? undefined,
    }))
    .filter(sheet => Boolean(sheet.name));
};

const getRelationships = (workbook: XlsxCfbWorkbook, fileName: string): XlsxWorkbookRelationship[] => {
  const relationshipsDocument = parseXmlDocument(getCfbTextFile(workbook, fileName));

  if (!relationshipsDocument) {
    return [];
  }

  return Array.from(relationshipsDocument.querySelectorAll('*'))
    .filter(node => node.localName === 'Relationship')
    .map(node => {
      const target = node.getAttribute('Target') ?? '';
      const targetFileName = getTargetFileName(target);

      return {
        id: node.getAttribute('Id') ?? '',
        target,
        targetFileName,
        type: node.getAttribute('Type') ?? undefined,
      };
    })
    .filter(relationship => Boolean(relationship.id) && Boolean(relationship.targetFileName));
};

const getWorkbookRelationships = (workbook: XlsxCfbWorkbook): XlsxWorkbookRelationship[] =>
  getRelationships(workbook, 'workbook.xml.rels');

const getSheetFileNameByName = (workbook: XlsxCfbWorkbook, sheetNames: string[]) => {
  const relationships = getWorkbookRelationships(workbook);
  const sheetReferences = getWorkbookSheetReferences(workbook);

  if (!relationships.length || !sheetReferences.length) {
    const fallbackSheetFileNameByName: Record<string, string> = {};

    sheetNames.forEach((sheetName, index) => {
      fallbackSheetFileNameByName[sheetName] = `sheet${index + 1}.xml`;
    });

    return fallbackSheetFileNameByName;
  }

  const sheetFileNameByName: Record<string, string> = {};

  sheetReferences.forEach((sheetReference, index) => {
    const relationship = relationships.find(item => item.id === sheetReference.relationshipId);

    sheetFileNameByName[sheetReference.name] = relationship?.targetFileName ?? `sheet${index + 1}.xml`;
  });

  return sheetFileNameByName;
};

const parseDrawingMarker = (node?: Element): SpreadsheetNoteXlsxImage['from'] | undefined => {
  if (!node) {
    return undefined;
  }

  const column = Number(getDirectChildByLocalName(node, 'col')?.textContent ?? NaN);
  const row = Number(getDirectChildByLocalName(node, 'row')?.textContent ?? NaN);

  if (!Number.isFinite(column) || !Number.isFinite(row)) {
    return undefined;
  }

  return {
    column,
    columnOffsetPx: emuToPx(getDirectChildByLocalName(node, 'colOff')?.textContent),
    row,
    rowOffsetPx: emuToPx(getDirectChildByLocalName(node, 'rowOff')?.textContent),
  };
};

const parseDrawingSize = (node?: Element): SpreadsheetNoteXlsxImage['size'] | undefined => {
  if (!node) {
    return undefined;
  }

  const widthPx = getPositiveSize(emuToPx(node.getAttribute('cx')));
  const heightPx = getPositiveSize(emuToPx(node.getAttribute('cy')));

  return widthPx && heightPx ? { widthPx, heightPx } : undefined;
};

const getDrawingRelationship = (relationships: XlsxWorkbookRelationship[], relationshipId?: string) =>
  relationships.find(relationship => relationship.id === relationshipId) ??
  relationships.find(relationship => relationship.type?.includes('/drawing'));

const getDrawingImageName = (anchorNode: Element) => {
  const nonVisualProperty = getFirstElementByLocalName(anchorNode, 'cNvPr');

  return nonVisualProperty?.getAttribute('descr') ?? nonVisualProperty?.getAttribute('name') ?? 'Embedded image';
};

const getDrawingImages = (
  workbook: XlsxCfbWorkbook,
  drawingFileName: string,
  sheetName: string
): SpreadsheetNoteXlsxImage[] => {
  const drawingDocument = parseXmlDocument(getCfbTextFile(workbook, drawingFileName));

  if (!drawingDocument) {
    return [];
  }

  const drawingRelationships = getRelationships(workbook, getRelationshipFileName(drawingFileName));
  const anchorNodes = Array.from(drawingDocument.querySelectorAll('*')).filter(
    node => node.localName === 'twoCellAnchor' || node.localName === 'oneCellAnchor'
  );

  return anchorNodes.reduce<SpreadsheetNoteXlsxImage[]>((images, anchorNode, anchorIndex) => {
    const from = parseDrawingMarker(getDirectChildByLocalName(anchorNode, 'from'));
    const embedId = getNamespacedAttribute(getFirstElementByLocalName(anchorNode, 'blip'), 'embed');

    if (!from || !embedId) {
      return images;
    }

    const mediaRelationship = drawingRelationships.find(relationship => relationship.id === embedId);
    const mediaFileName = mediaRelationship?.targetFileName;
    const contentType = mediaFileName ? getImageContentType(mediaFileName) : undefined;
    const mediaBytes = mediaFileName ? getCfbBinaryFile(workbook, mediaFileName) : undefined;

    if (!mediaFileName || !contentType || !mediaBytes) {
      return images;
    }

    const to = parseDrawingMarker(getDirectChildByLocalName(anchorNode, 'to'));
    const size = parseDrawingSize(getFirstElementByLocalName(anchorNode, 'ext'));

    return [
      ...images,
      {
        alt: getDrawingImageName(anchorNode),
        contentType,
        from,
        id: `${sheetName}-${mediaFileName}-${anchorIndex}`,
        size,
        src: `data:${contentType};base64,${uint8ArrayToBase64(mediaBytes)}`,
        ...(to ? { to } : {}),
      },
    ];
  }, []);
};

const getSheetPresentationImages = (
  workbook: XlsxCfbWorkbook | undefined,
  sheetFileName: string | undefined,
  sheetName: string
) => {
  if (!workbook || !sheetFileName) {
    return [];
  }

  const sheetDocument = parseXmlDocument(getCfbTextFile(workbook, sheetFileName));
  const drawingNode = sheetDocument ? getFirstElementByLocalName(sheetDocument, 'drawing') : undefined;
  const drawingRelationshipId = getNamespacedAttribute(drawingNode, 'id');

  if (!drawingRelationshipId) {
    return [];
  }

  const sheetRelationships = getRelationships(workbook, getRelationshipFileName(sheetFileName));
  const drawingRelationship = getDrawingRelationship(sheetRelationships, drawingRelationshipId);

  return drawingRelationship ? getDrawingImages(workbook, drawingRelationship.targetFileName, sheetName) : [];
};

const getWorkbookStyleContext = (
  arrayBuffer: ArrayBuffer,
  sheetNames: string[],
  workbookOverride?: XlsxCfbWorkbook
): XlsxWorkbookStyleContext => {
  const cfbWorkbook = workbookOverride ?? getXlsxCfbWorkbook(arrayBuffer);

  if (!cfbWorkbook) {
    return {
      sheetStyles: {},
      themeColors: EXCEL_DEFAULT_THEME_COLORS,
    };
  }

  const themeColors = getWorkbookThemeColors(cfbWorkbook);
  const cellXfs = getWorkbookCellXfs(cfbWorkbook);
  const sheetFileNameByName = getSheetFileNameByName(cfbWorkbook, sheetNames);
  const sheetStyles: XlsxWorkbookStyleContext['sheetStyles'] = {};

  sheetNames.forEach(sheetName => {
    const sheetDocument = parseXmlDocument(getCfbTextFile(cfbWorkbook, sheetFileNameByName[sheetName]));

    if (!sheetDocument) {
      return;
    }

    const cellStyles: XlsxRawCellStyleMap = {};

    Array.from(sheetDocument.querySelectorAll('*')).forEach(node => {
      if (node.localName !== 'c') {
        return;
      }

      const address = node.getAttribute('r');
      const styleId = node.getAttribute('s');
      const style = styleId == null ? undefined : cellXfs[Number(styleId)];

      if (!address || !style) {
        return;
      }

      cellStyles[address] = style;
    });

    sheetStyles[sheetName] = cellStyles;
  });

  return {
    sheetStyles,
    themeColors,
  };
};

const getFillOverride = (style: XlsxRawCellStyle, themeColors: string[]): Fill | undefined => {
  const { fill } = style;
  const foreground = normalizeColor(fill?.fgColor ?? style.fgColor, themeColors);
  const background = normalizeColor(fill?.bgColor ?? style.bgColor, themeColors);
  const patternType = fill?.patternType ?? style.patternType;

  if (!foreground && !background && !patternType) {
    return undefined;
  }

  return {
    pattern_type: patternType ?? null,
    bg_color: background ?? null,
    fg_color: foreground ?? null,
  };
};

const getFontOverride = (style: XlsxRawCellStyle, themeColors: string[]): Font | undefined => {
  const { font } = style;

  if (!font) {
    return undefined;
  }

  const color = normalizeColor(font.color, themeColors);
  const hasFont =
    font.name != null ||
    font.sz != null ||
    font.b != null ||
    font.bold != null ||
    font.i != null ||
    font.italic != null ||
    color != null;

  if (!hasFont) {
    return undefined;
  }

  return {
    name: font.name ?? null,
    b: Boolean(font.bold ?? font.b),
    i: Boolean(font.italic ?? font.i),
    color: color ?? null,
    size: font.sz ?? null,
  };
};

const getAlignmentOverride = (style: XlsxRawCellStyle): Alignment | undefined => {
  if (!style.alignment) {
    return undefined;
  }

  return {
    horizontal: style.alignment.horizontal ?? null,
    vertical: style.alignment.vertical ?? null,
    wrap_text: style.alignment.wrapText ?? null,
  };
};

const getBorderSideOverride = (side?: XlsxRawBorderSide, themeColors = EXCEL_DEFAULT_THEME_COLORS) => {
  if (!side?.style) {
    return undefined;
  }

  return {
    color: normalizeColor(side.color, themeColors) ?? null,
    style: side.style,
  };
};

const getBorderOverride = (style: XlsxRawCellStyle, themeColors: string[]): XlsxCellBorder | undefined => {
  if (!style.border) {
    return undefined;
  }

  const border = {
    bottom: getBorderSideOverride(style.border.bottom, themeColors),
    left: getBorderSideOverride(style.border.left, themeColors),
    right: getBorderSideOverride(style.border.right, themeColors),
    top: getBorderSideOverride(style.border.top, themeColors),
  };

  return Object.values(border).some(Boolean) ? border : undefined;
};

const getCellOverride = (
  rawStyle?: XlsxRawCellStyle,
  themeColors: string[] = EXCEL_DEFAULT_THEME_COLORS
): XlsxCellOverride | undefined => {
  if (!rawStyle) {
    return undefined;
  }

  const fill = getFillOverride(rawStyle, themeColors);
  const font = getFontOverride(rawStyle, themeColors);
  const alignment = getAlignmentOverride(rawStyle);
  const border = getBorderOverride(rawStyle, themeColors);

  if (!fill && !font && !alignment && !border) {
    return undefined;
  }

  return {
    ...(fill ? { fill } : {}),
    ...(font ? { font } : {}),
    ...(alignment ? { alignment } : {}),
    ...(border ? { border } : {}),
  };
};

const getColumnWidthOverride = (columnInfo?: ColInfo): ColumnData | undefined => {
  if (!columnInfo) {
    return undefined;
  }

  if (columnInfo.hidden) {
    return { column_width: 0 };
  }

  const columnWidth =
    columnInfo.width ??
    columnInfo.wch ??
    (columnInfo.wpx ? (columnInfo.wpx - EXCEL_COLUMN_WIDTH_PADDING_PX) / EXCEL_COLUMN_WIDTH_TO_PX : undefined);

  return typeof columnWidth === 'number' && Number.isFinite(columnWidth)
    ? { column_width: clamp(columnWidth, XLSX_MIN_COLUMN_WIDTH, XLSX_MAX_COLUMN_WIDTH) }
    : undefined;
};

const getRowHeightOverride = (rowInfo?: RowInfo): RowData | undefined => {
  if (!rowInfo) {
    return undefined;
  }

  if (rowInfo.hidden) {
    return { row_height: 0 };
  }

  const rowHeight = rowInfo.hpt ?? (rowInfo.hpx ? rowInfo.hpx / EXCEL_ROW_HEIGHT_TO_PX : undefined);

  return typeof rowHeight === 'number' && Number.isFinite(rowHeight)
    ? { row_height: clamp(rowHeight, XLSX_MIN_ROW_HEIGHT, XLSX_MAX_ROW_HEIGHT) }
    : undefined;
};

const getPreviewRange = (worksheet: WorkSheet) => {
  const sheetRange = worksheet['!ref'];

  if (!sheetRange) {
    return undefined;
  }

  const decodedRange = utils.decode_range(sheetRange);

  return {
    e: {
      c: Math.min(decodedRange.e.c, decodedRange.s.c + XLSX_MAX_PRESENTATION_COLUMNS - 1),
      r: Math.min(decodedRange.e.r, decodedRange.s.r + XLSX_MAX_PRESENTATION_ROWS - 1),
    },
    s: decodedRange.s,
  };
};

const parseSheetPresentationOverrides = (
  worksheet: WorkSheet,
  styleContext: XlsxWorkbookStyleContext,
  sheetName: string,
  cfbWorkbook?: XlsxCfbWorkbook,
  sheetFileName?: string
): XlsxSheetPresentationOverride => {
  const previewRange = getPreviewRange(worksheet);
  const cells: XlsxSheetPresentationOverride['cells'] = {};
  const columnData: XlsxSheetPresentationOverride['columnData'] = {};
  const images = getSheetPresentationImages(cfbWorkbook, sheetFileName, sheetName);
  const rowData: XlsxSheetPresentationOverride['rowData'] = {};

  if (!previewRange) {
    return {
      cells,
      columnData,
      images,
      rowData,
    };
  }

  const columns = worksheet['!cols'] as ColInfo[] | undefined;
  const rowsInfo = worksheet['!rows'] as RowInfo[] | undefined;
  const sheetStyles = styleContext.sheetStyles[sheetName] ?? {};

  for (let rowIndex = previewRange.s.r; rowIndex <= previewRange.e.r; rowIndex += 1) {
    const rowHeight = getRowHeightOverride(rowsInfo?.[rowIndex]);
    if (rowHeight) {
      rowData[rowIndex] = rowHeight;
    }

    for (let columnIndex = previewRange.s.c; columnIndex <= previewRange.e.c; columnIndex += 1) {
      const address = utils.encode_cell({ c: columnIndex, r: rowIndex });
      const cell = worksheet[address];
      const rawStyle = mergeRawCellStyles(cell?.s as XlsxRawCellStyle | undefined, sheetStyles[address]);
      const cellOverride = getCellOverride(rawStyle, styleContext.themeColors);

      if (cellOverride) {
        cells[address] = cellOverride;
      }
    }
  }

  for (let columnIndex = previewRange.s.c; columnIndex <= previewRange.e.c; columnIndex += 1) {
    const columnWidth = getColumnWidthOverride(columns?.[columnIndex]);
    if (columnWidth) {
      columnData[columnIndex] = columnWidth;
    }
  }

  return {
    cells,
    columnData,
    images,
    rowData,
  };
};

export const decodeXlsxPresentationBase64Content = (content: string) => {
  const binaryString = atob(content);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return bytes.buffer;
};

export const parseXlsxPresentationOverrides = (arrayBuffer: ArrayBuffer): XlsxPresentationOverrides => {
  const workbook = read(arrayBuffer, {
    cellDates: true,
    cellNF: true,
    cellStyles: true,
    cellText: true,
    dateNF: 'm/d/yyyy',
    sheetStubs: true,
    type: 'array',
  });
  const sheetNames = workbook.SheetNames.filter(sheetName => Boolean(workbook.Sheets[sheetName]));
  const cfbWorkbook = getXlsxCfbWorkbook(arrayBuffer);
  const styleContext = getWorkbookStyleContext(arrayBuffer, sheetNames, cfbWorkbook);
  const sheetFileNameByName = cfbWorkbook ? getSheetFileNameByName(cfbWorkbook, sheetNames) : {};

  return {
    sheetNames,
    sheets: sheetNames.reduce<XlsxPresentationOverrides['sheets']>((sheets, sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      if (worksheet) {
        return {
          ...sheets,
          [sheetName]: parseSheetPresentationOverrides(
            worksheet,
            styleContext,
            sheetName,
            cfbWorkbook,
            sheetFileNameByName[sheetName]
          ),
        };
      }

      return sheets;
    }, {}),
  };
};

const getSheetOverrideForNote = (
  overrides: XlsxPresentationOverrides,
  note: SpreadsheetNoteData,
  noteIndex: number
) => {
  const sheetName = note.name ?? overrides.sheetNames[noteIndex];

  return sheetName ? overrides.sheets[sheetName] : undefined;
};

const isEmptyMergedCell = (cells: SpreadsheetNoteData['content']['cells'], mergedCell: MergedCell) => {
  const cell = cells?.[mergedCell.top_left];

  return !String(cell?.value ?? cell?.expr ?? '').trim();
};

const getPreviewMergedCells = (note: SpreadsheetNoteData, maxEmptyMergedRowSpan = XLSX_MAX_EMPTY_MERGED_ROW_SPAN) =>
  note.content.merged_cells?.filter(mergedCell => {
    // Decorative vertical spacer merges can span most of a sheet and make a readonly preview unusably tall.
    if (
      mergedCell.row_span > maxEmptyMergedRowSpan &&
      mergedCell.col_span <= 1 &&
      isEmptyMergedCell(note.content.cells, mergedCell)
    ) {
      return false;
    }

    return true;
  });

export const applyXlsxPresentationOverridesToNotes = (
  notes: SpreadsheetNoteData[],
  overrides?: XlsxPresentationOverrides,
  options?: ApplyXlsxPresentationOverridesOptions
): SpreadsheetNoteData[] => {
  if (!overrides) {
    return notes;
  }

  return notes.map((note, noteIndex) => {
    const sheetOverride = getSheetOverrideForNote(overrides, note, noteIndex);

    if (!sheetOverride) {
      return note;
    }

    const cells = { ...(note.content.cells ?? {}) };
    const columnData = { ...(note.content.column_data ?? {}) };
    const images = sheetOverride.images ?? [];
    const rowData = { ...(note.content.row_data ?? {}) };

    Object.entries(sheetOverride.cells).forEach(([address, cellOverride]) => {
      cells[address] = {
        value: '',
        expr: '',
        ...(cells[address] ?? {}),
        ...cellOverride,
      } as Cell;
    });

    Object.entries(sheetOverride.columnData).forEach(([index, columnOverride]) => {
      columnData[index] = {
        ...(columnData[index] ?? {}),
        ...columnOverride,
      };
    });

    Object.entries(sheetOverride.rowData).forEach(([index, rowOverride]) => {
      rowData[index] = {
        ...(rowData[index] ?? {}),
        ...rowOverride,
      };
    });

    return {
      ...note,
      ...(images.length ? { xlsxPresentationImages: images } : {}),
      content: {
        ...note.content,
        cells,
        column_data: columnData,
        row_data: rowData,
        merged_cells: getPreviewMergedCells(note, options?.maxEmptyMergedRowSpan),
      },
    };
  });
};
